<template>
  <v-dialog v-model="visible" max-width="600" scrollable persistent content-class="ds-wizard-dialog">
    <v-card v-if="type" class="ds-dialog">
      <div class="ds-dialog__header">
        <div class="ds-dialog__header-icon">
          <v-icon color="warning">mdi-alert-outline</v-icon>
        </div>
        <div class="ds-dialog__header-text">
          <span class="ds-dialog__title">Resolve before allowing one zone</span>
          <span class="ds-dialog__subtitle">
            {{ total }} {{ total === 1 ? 'record is' : 'records are' }} in more than one {{ type.name }} zone
          </span>
        </div>
        <v-btn icon small title="Close" aria-label="Close" @click="finish(false)">
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </div>

      <v-card-text class="resolve-body">
        <v-alert v-if="hidden" type="warning" dense text class="mb-3">
          {{ hidden }} of them {{ hidden === 1 ? 'is' : 'are' }} in facilities or of kinds you cannot open. Someone with
          access has to resolve {{ hidden === 1 ? 'it' : 'them' }} before {{ type.name }} can allow only one zone.
        </v-alert>
        <p v-if="violations.length" class="caption mb-2">
          Choose the zone each record keeps. Its other {{ type.name }} assignments are removed when you apply.
        </p>
        <div v-for="violation in violations" :key="violation.facilityId + violation.recordType + violation.recordId" class="violation">
          <div class="d-flex align-center">
            <v-icon small class="mr-1">{{ violation.recordType === 'task' ? 'mdi-clipboard-check-outline' : 'mdi-tag-outline' }}</v-icon>
            <span class="font-weight-medium">{{ violation.recordName }}</span>
            <v-spacer />
            <span class="caption">{{ violation.facilityName }}</span>
          </div>
          <v-radio-group v-model="keep[keyOf(violation)]" dense hide-details class="mt-1">
            <v-radio v-for="link in violation.assignments" :key="link.assignmentId" :value="link.assignmentId">
              <template v-slot:label>
                {{ link.zoneName }}<span v-if="link.zoneStatus === 'retired'" class="caption ml-1">(retired)</span>
              </template>
            </v-radio>
          </v-radio-group>
        </div>
      </v-card-text>

      <div class="ds-dialog__footer">
        <span class="ds-dialog__hint">{{ chosen }} of {{ violations.length }} chosen</span>
        <v-spacer />
        <v-btn text @click="finish(false)">Cancel</v-btn>
        <v-btn color="primary" depressed :disabled="!canApply" :loading="applying" @click="apply">
          Apply and allow one zone
        </v-btn>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
  import zonesService from '../services/zones.service';

  /**
   * Making a zone type one-zone-only (AVA-764): the stricter rule takes effect only after every record
   * that breaks it is resolved. Lists those records, takes one kept zone per record (no default: the
   * choice is explicit) and applies it with the rule in one API call. `open(type, violations)`
   * resolves to true once the type allows only one zone.
   */
  export default {
    name: 'ZoneRuleResolveDialog',
    data() {
      return { visible: false, type: null, violations: [], hidden: 0, keep: {}, applying: false, done: null };
    },
    computed: {
      total() {
        return this.violations.length + this.hidden;
      },
      chosen() {
        return this.violations.filter((v) => this.keep[this.keyOf(v)]).length;
      },
      canApply() {
        return !this.hidden && this.violations.length > 0 && this.chosen === this.violations.length;
      },
    },
    methods: {
      keyOf(violation) {
        return `${violation.facilityId}|${violation.recordType}|${violation.recordId}`;
      },
      open(type, { violations = [], hidden = 0 }) {
        this.type = type;
        this.violations = violations;
        this.hidden = hidden;
        this.keep = {};
        this.visible = true;
        return new Promise((resolve) => {
          this.done = resolve;
        });
      },
      finish(restricted) {
        this.visible = false;
        const done = this.done;
        this.done = null;
        if (done) done(restricted);
      },
      async apply() {
        this.applying = true;
        try {
          await zonesService.restrictTypeToSingle(this.type.id, Object.values(this.keep));
          this.finish(true);
        } catch (err) {
          this.$dialog.notify.error(err.message);
        } finally {
          this.applying = false;
        }
      },
    },
  };
</script>

<style scoped lang="scss">
  .resolve-body {
    max-height: 60vh;
    padding: var(--ds-space-md) var(--ds-space-lg) !important;
  }

  .violation {
    padding: var(--ds-space-sm);
    margin-bottom: var(--ds-space-sm);
    border: 1px solid var(--v-bg7-base);
    border-radius: var(--ds-radius-md);
  }
</style>
